import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { ENV_AMORTIZED_EMBODIED_CAPTION } from '@siliconx/shared';
import type { EnvironmentalMonthlyPoint, EnvironmentalS3MonthlyFlowPoint } from '@siliconx/shared';
import { SX_PALETTE } from '@siliconx/shared/ui/charts';
import { useAmortizedEmbodiedMonthlySeries, useS3MonthlyFlows } from '../../hooks/useEnvironmentalOverview';
import { SAMPLE_S3_MONTHLY_FLOWS_KGCO2E } from '../../lib/environmentalSampleData';
import { BasisTag } from './BasisTag';
import { formatTonnes } from './format';

const BORDER = '#e4e4e7';
const MUTED = '#71717a';

// Scope 3 flow-category ramp (warm family, distinct from Scope 2's indigo).
const FLOW_COLORS = { cat4: '#f59e0b', cat5: '#d97706', cat11: '#fb7185', cat12: '#e11d48' };

/**
 * Monthly emissions as SMALL MULTIPLES by scope (demo-feedback prototype
 * decision, variant B): Scope 2 alone on top at its own scale; Scope 3
 * monthly flows stacked by category below; the cat 1/2 embodied STOCK is
 * stated in a chip, never plotted — a stock repeated per month is not a
 * monthly flow, and plotting it either compresses the flows (shared axis)
 * or fakes a trend.
 *
 * Data honesty: the Scope 2 panel is REAL (the org's computed use-phase
 * months — the card header's basis tag covers it). SIL-502 adds a SECOND
 * top-panel series, `amortizedEmbodiedKgco2e` (`useAmortizedEmbodiedMonthlySeries`
 * / `apps/api/src/lib/amortized-embodied-monthly.ts`) — real when the org has
 * a derivable series (`null` -> the second bar is simply omitted, no sample
 * fallback for this field). This is a DUAL-LENS surface: the amortized bars
 * are a platform MANAGEMENT CONVENTION (72-month straight-line spread of the
 * embodied stock), never a change to the GHG INVENTORY convention the stock
 * chip states (embodied recognized in full at acquisition, GHG Protocol
 * Scope 3 Standard §5.4, Box 5.3) — the caption below states both halves of
 * that rule verbatim (`ENV_AMORTIZED_EMBODIED_CAPTION`) whenever the stock
 * chip is shown. The Scope 3 flow panel (bottom) is HYBRID (SIL-489): REAL
 * per-category monthly flows (cat 4 transport, cat 5 disposal, cat 11
 * use-of-sold, cat 12 end-of-life — see
 * `useS3MonthlyFlows`/`apps/api/src/lib/s3-monthly-flows.ts` for the
 * per-category formula lineage) when the org has a derivable monthly
 * series, SAMPLE fallback (unchanged) otherwise — its own basis tag below
 * tracks which. The stock chip's figure is REAL (the same embodied
 * aggregate the KPI strip shows, carried on every point of the series) and
 * is NEVER removed — SIL-502 adds the amortized lens alongside it, never in
 * place of it.
 */
export function MonthlySeriesChart({ points }: { points: EnvironmentalMonthlyPoint[] }) {
  const s3Flows = useS3MonthlyFlows();
  const amortized = useAmortizedEmbodiedMonthlySeries();

  if (points.length === 0) {
    return (
      <div className="flex h-48 items-center justify-center text-xs text-zinc-400">
        No monthly use-phase history computed yet.
      </div>
    );
  }

  const stockKgco2e = points[points.length - 1]?.scope3Kgco2e ?? null;
  const realFlows: EnvironmentalS3MonthlyFlowPoint[] | null = s3Flows.data;
  const flowData =
    realFlows ??
    points.map((p, i) => {
      const f = SAMPLE_S3_MONTHLY_FLOWS_KGCO2E[i % SAMPLE_S3_MONTHLY_FLOWS_KGCO2E.length]!;
      return { periodKey: p.periodKey, ...f };
    });
  const flowBasis = s3Flows.basis ?? 'sample';

  const amortizedByPeriodKey = new Map((amortized.data ?? []).map((p) => [p.periodKey, p.amortizedEmbodiedKgco2e]));
  const topPanelData = points.map((p) => ({
    ...p,
    amortizedEmbodiedKgco2e: amortized.data ? (amortizedByPeriodKey.get(p.periodKey) ?? 0) : null,
  }));

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-widest text-zinc-400">
          Scope 2 · electricity
        </span>
        {stockKgco2e != null && (
          <span className="rounded bg-zinc-100 px-1.5 py-0.5 text-xs font-medium text-zinc-600">
            Cat 1/2 embodied stock: {formatTonnes(stockKgco2e)} (not a monthly flow — reported
            separately)
          </span>
        )}
      </div>
      <ResponsiveContainer width="100%" height={120}>
        <BarChart data={topPanelData} margin={{ top: 4, right: 16, bottom: 0, left: 8 }} syncId="env-monthly">
          <CartesianGrid strokeDasharray="3 3" stroke={BORDER} vertical={false} />
          <XAxis dataKey="periodKey" hide />
          <YAxis
            stroke={MUTED}
            fontSize={12}
            tickLine={false}
            width={56}
            tickFormatter={(v: number) => formatTonnes(v)}
          />
          <Tooltip
            contentStyle={{ fontSize: 12, borderRadius: 6, borderColor: BORDER }}
            formatter={(v: number) => formatTonnes(v)}
          />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <Bar dataKey="scope2Kgco2e" name="Scope 2 · electricity" fill={SX_PALETTE[1]} />
          {amortized.data && (
            <Bar
              dataKey="amortizedEmbodiedKgco2e"
              name="Scope 3 · embodied (amortized)"
              fill={FLOW_COLORS.cat4}
            />
          )}
        </BarChart>
      </ResponsiveContainer>
      {stockKgco2e != null && (
        <p className="text-xs leading-relaxed text-zinc-500">{ENV_AMORTIZED_EMBODIED_CAPTION}</p>
      )}

      <div className="flex items-center gap-2">
        <span className="text-xs font-semibold uppercase tracking-widest text-zinc-400">
          Scope 3 · monthly flows by category
        </span>
        <BasisTag basis={flowBasis} />
      </div>
      <ResponsiveContainer width="100%" height={150}>
        <BarChart data={flowData} margin={{ top: 4, right: 16, bottom: 4, left: 8 }} syncId="env-monthly">
          <CartesianGrid strokeDasharray="3 3" stroke={BORDER} vertical={false} />
          <XAxis dataKey="periodKey" stroke={MUTED} fontSize={12} tickLine={false} />
          <YAxis
            stroke={MUTED}
            fontSize={12}
            tickLine={false}
            width={56}
            tickFormatter={(v: number) => formatTonnes(v)}
          />
          <Tooltip
            contentStyle={{ fontSize: 12, borderRadius: 6, borderColor: BORDER }}
            formatter={(v: number) => formatTonnes(v)}
          />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <Bar dataKey="cat4Kgco2e" name="cat 4 · transport" stackId="s3" fill={FLOW_COLORS.cat4} />
          <Bar dataKey="cat5Kgco2e" name="cat 5 · disposal" stackId="s3" fill={FLOW_COLORS.cat5} />
          <Bar dataKey="cat11Kgco2e" name="cat 11 · use of sold" stackId="s3" fill={FLOW_COLORS.cat11} />
          <Bar dataKey="cat12Kgco2e" name="cat 12 · end-of-life" stackId="s3" fill={FLOW_COLORS.cat12} />
        </BarChart>
      </ResponsiveContainer>
      <p className="text-xs leading-relaxed text-zinc-500">
        Scopes shown on separate aligned panels, each at its own scale.{' '}
        {realFlows
          ? "The Scope 3 flow panel is derived from this org's own fleet events and completed sales; the embodied stock in the chip is the fleet's real current figure."
          : "The Scope 3 flow panel is sample data — per-category monthly flow series are not computed yet; the embodied stock in the chip is the fleet's real current figure."}
      </p>
    </div>
  );
}
